// Encharge sync helper (client side).
//
// Calls the `encharge-sync` edge function so the Encharge API key never
// leaves the server. All calls are fire-and-forget from the caller's POV:
// the helpers themselves are async, but every internal error is swallowed
// and logged so a slow / failing Encharge never blocks UI flows.
//
// Pairs with analytics.js's `capture()` wrapper (which dual-writes every
// PostHog event here) plus a small number of explicit `identifyUser` calls
// at the moments user attributes change.

import { supabase } from '../lib/supabase';

const ENCHARGE_FUNCTION = 'encharge-sync';

const isDev = () => Boolean(import.meta?.env?.DEV);

const splitName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return { firstName: null, lastName: null };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: parts[0], lastName: null };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const invoke = async (payload) => {
  try {
    const { error } = await supabase.functions.invoke(ENCHARGE_FUNCTION, { body: payload });
    if (error) {
      console.warn('[encharge] sync failed:', error.message ?? error);
      return;
    }
    if (isDev()) console.log('[encharge]', payload.op, payload.name ?? payload.email);
  } catch (err) {
    console.warn('[encharge] sync threw:', err);
  }
};

/**
 * Identify a user in Encharge - upserts the person by email and writes the
 * given attributes. Safe to call repeatedly; Encharge dedupes by email.
 *
 * @param {object} user - Supabase auth user (must have .email)
 * @param {object} [extraAttributes] - Additional fields to merge
 */
export const identifyUser = (user, extraAttributes = {}) => {
  if (!user?.email) return;

  const fullName = user.user_metadata?.full_name ?? null;
  const { firstName, lastName } = splitName(fullName);

  const attributes = {
    userId: user.id,
    createdAt: user.created_at,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...extraAttributes,
  };

  return invoke({ op: 'identify', email: user.email, attributes });
};

/**
 * Track an event in Encharge against the given user's email.
 *
 * @param {object} user - Supabase auth user (must have .email)
 * @param {string} name - Event name (snake_case to match PostHog convention)
 * @param {object} [properties] - Event properties
 */
export const trackEvent = (user, name, properties = {}) => {
  if (!user?.email || !name) return;
  return invoke({ op: 'track', email: user.email, name, properties });
};
